export const ROOT_SERVICE_WORKER_URL = "/sw.js";
export const ROOT_SERVICE_WORKER_SCOPE = "/";
export const SERVICE_WORKER_READY_WAIT_MS = 10000;

let registrationPromise = null;

function defaultNavigator() {
  return typeof navigator === "undefined" ? null : navigator;
}

function defaultWindow() {
  return typeof window === "undefined" ? null : window;
}

function workerScriptPath(registration, windowObject) {
  const worker =
    registration?.installing || registration?.waiting || registration?.active;
  if (!worker?.scriptURL) return null;
  try {
    return new URL(
      worker.scriptURL,
      windowObject?.location?.href || "https://reelitpro.invalid/"
    ).pathname;
  } catch {
    return null;
  }
}

function hasExpectedRootWorker(registration, windowObject) {
  if (!registration) return false;
  try {
    const scopePath = new URL(
      registration.scope,
      windowObject?.location?.href || "https://reelitpro.invalid/"
    ).pathname;
    return (
      scopePath === ROOT_SERVICE_WORKER_SCOPE &&
      workerScriptPath(registration, windowObject) === ROOT_SERVICE_WORKER_URL
    );
  } catch {
    return false;
  }
}

function result(status, registration = null) {
  return {
    status,
    code:
      status === "ready"
        ? null
        : status === "unsupported"
          ? "service_worker_unsupported"
          : "service_worker_unavailable",
    registration,
  };
}

function hasExpectedActiveRootWorker(registration, windowObject) {
  if (!registration?.active) return false;
  try {
    const scopePath = new URL(
      registration.scope,
      windowObject?.location?.href || "https://reelitpro.invalid/"
    ).pathname;
    return (
      scopePath === ROOT_SERVICE_WORKER_SCOPE &&
      workerScriptPath({ active: registration.active }, windowObject) ===
        ROOT_SERVICE_WORKER_URL
    );
  } catch {
    return false;
  }
}

function activationWait(registration, windowObject) {
  const candidate = registration?.installing || registration?.waiting;
  if (!candidate || typeof candidate.addEventListener !== "function") {
    return {
      promise: new Promise(() => {}),
      cancel: () => {},
    };
  }
  let handleStateChange;
  const promise = new Promise((resolve) => {
    handleStateChange = () => {
      if (
        candidate.state === "activated" &&
        hasExpectedActiveRootWorker(registration, windowObject)
      ) {
        candidate.removeEventListener?.("statechange", handleStateChange);
        handleStateChange = null;
        resolve(registration);
      }
    };
    candidate.addEventListener("statechange", handleStateChange);
    handleStateChange();
  });
  return {
    promise,
    cancel: () => {
      if (handleStateChange) {
        candidate.removeEventListener?.("statechange", handleStateChange);
        handleStateChange = null;
      }
    },
  };
}

async function waitForActiveRootWorker(
  serviceWorker,
  registration,
  windowObject,
  waitMs = SERVICE_WORKER_READY_WAIT_MS
) {
  if (hasExpectedActiveRootWorker(registration, windowObject)) {
    return registration;
  }

  const readyPromise = serviceWorker?.ready?.then
    ? serviceWorker.ready.then((readyRegistration) =>
        hasExpectedActiveRootWorker(readyRegistration, windowObject)
          ? readyRegistration
          : new Promise(() => {})
      )
    : new Promise(() => {});
  const activation = activationWait(registration, windowObject);
  let timeoutId;
  try {
    return await Promise.race([
      readyPromise,
      activation.promise,
      new Promise((resolve) => {
        timeoutId = setTimeout(() => resolve(null), waitMs);
      }),
    ]);
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
    activation.cancel();
  }
}

async function registerRootServiceWorkerOnce(options) {
  const navigatorObject = options.navigatorObject ?? defaultNavigator();
  const windowObject = options.windowObject ?? defaultWindow();
  const serviceWorker = navigatorObject?.serviceWorker;

  if (!serviceWorker || typeof serviceWorker.register !== "function") {
    return result("unsupported");
  }

  if (typeof serviceWorker.getRegistration === "function") {
    try {
      const existing = await serviceWorker.getRegistration(
        ROOT_SERVICE_WORKER_SCOPE
      );
      if (hasExpectedRootWorker(existing, windowObject)) {
        if (typeof existing.update === "function") {
          try {
            await existing.update();
          } catch {
            // The active worker remains usable when an update check fails.
          }
        }
        const readyRegistration = await waitForActiveRootWorker(
          serviceWorker,
          existing,
          windowObject,
          options.readyWaitMs
        );
        return readyRegistration
          ? result("ready", readyRegistration)
          : result("unavailable");
      }
    } catch {
      // Fall through to the idempotent register call for the root scope.
    }
  }

  try {
    const registration = await serviceWorker.register(
      ROOT_SERVICE_WORKER_URL,
      {
        scope: ROOT_SERVICE_WORKER_SCOPE,
        updateViaCache: "none",
      }
    );
    const readyRegistration = await waitForActiveRootWorker(
      serviceWorker,
      registration,
      windowObject,
      options.readyWaitMs
    );
    return readyRegistration
      ? result("ready", readyRegistration)
      : result("unavailable");
  } catch {
    return result("unavailable");
  }
}

export function registerRootServiceWorker(options = {}) {
  if (!registrationPromise) {
    let attempt;
    attempt = registerRootServiceWorkerOnce(options).then((registrationResult) => {
      if (
        registrationPromise === attempt &&
        registrationResult.status !== "ready"
      ) {
        registrationPromise = null;
      }
      return registrationResult;
    });
    registrationPromise = attempt;
  }
  return registrationPromise;
}

export function getRootServiceWorkerRegistration(options = {}) {
  return registerRootServiceWorker(options);
}

// Drops only the cached initialization attempt. This is intentionally
// non-destructive: an in-flight worker may still finish installing, and a
// later caller can safely rediscover or register the same root worker.
export function invalidateRootServiceWorkerInitialization() {
  registrationPromise = null;
}

export function resetRootServiceWorkerRegistrationForTests() {
  invalidateRootServiceWorkerInitialization();
}
